"use client";

import { useState } from "react";
import { createClient } from "@/lib/supabase/client";
import Logo from "@/components/Logo";
import posthog from "posthog-js";

type Mode = "signin" | "signup" | "magic" | "reset";

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState<string | null>(null); // success message

  const appUrl =
    process.env.NEXT_PUBLIC_APP_URL?.replace(/\/$/, "") ??
    (typeof window !== "undefined" ? window.location.origin : "https://liferradar.com");

  const supabase = createClient();

  function reset() { setError(""); setDone(null); }
  function switchMode(m: Mode) { reset(); setMode(m); }

  // ── Sign in ────────────────────────────────────────────────────────────────
  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault(); setLoading(true); reset();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      posthog.captureException(error);
      setError(error.message);
    } else {
      posthog.capture("login_password_success");
      window.location.href = "/home";
    }
    setLoading(false);
  }

  // ── Sign up ────────────────────────────────────────────────────────────────
  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault(); setLoading(true); reset();
    const { error } = await supabase.auth.signUp({
      email, password,
      options: { emailRedirectTo: `${appUrl}/auth/callback` },
    });
    if (error) {
      posthog.captureException(error);
      setError(error.message);
    } else {
      posthog.capture("signup_password_initiated");
      setDone("Check your email to confirm your account.");
    }
    setLoading(false);
  }

  // ── Magic link ─────────────────────────────────────────────────────────────
  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault(); setLoading(true); reset();
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${appUrl}/auth/callback` },
    });
    if (error) {
      posthog.captureException(error);
      setError(error.message);
    } else {
      posthog.capture("login_magic_link_requested");
      setDone(`Magic link sent to ${email}`);
    }
    setLoading(false);
  }

  // ── Reset password ─────────────────────────────────────────────────────────
  async function handleReset(e: React.FormEvent) {
    e.preventDefault(); setLoading(true); reset();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${appUrl}/auth/callback?next=/profile`,
    });
    if (error) {
      posthog.captureException(error);
      setError(error.message);
    } else {
      setDone("Password reset link sent — check your email.");
    }
    setLoading(false);
  }

  // ── Google ─────────────────────────────────────────────────────────────────
  async function handleGoogle() {
    posthog.capture("login_google_initiated");
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${appUrl}/auth/callback` },
    });
  }

  const isSignIn = mode === "signin";
  const isSignUp = mode === "signup";
  const isMagic  = mode === "magic";
  const isReset  = mode === "reset";

  return (
    <div style={{
      minHeight: "100vh", display: "flex",
      alignItems: "center", justifyContent: "center",
      background: "linear-gradient(180deg, #f2f6f2 0%, #e8f2ec 100%)",
      padding: "24px",
    }}>
      <div style={{
        background: "#fff", borderRadius: "28px", padding: "40px",
        width: "100%", maxWidth: "420px",
        boxShadow: "0 8px 40px rgba(0,0,0,0.09)",
        border: "1px solid rgba(0,0,0,0.06)",
        fontFamily: "var(--font-nunito), sans-serif",
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            <Logo size="lg" href={false} />
          </div>
          <div style={{ fontSize: 14, color: "#4a6650", fontWeight: 600, marginTop: 4 }}>
            {isSignUp ? "Create your account" : isReset ? "Reset password" : isMagic ? "Sign in without password" : "Sign in to your collection"}
          </div>
        </div>

        {/* Success state */}
        {done ? (
          <div style={{
            background: "#e6f4ed", border: "1px solid rgba(45,138,94,0.3)",
            borderRadius: 16, padding: "20px", textAlign: "center",
          }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📬</div>
            <div style={{ fontWeight: 800, color: "#1a2b1d", marginBottom: 6 }}>{done}</div>
            <button
              onClick={() => { setDone(null); setMode("signin"); }}
              style={{
                marginTop: 12, fontSize: 13, fontWeight: 700, color: "#2d8a5e",
                background: "none", border: "none", cursor: "pointer",
              }}
            >
              ← Back to sign in
            </button>
          </div>
        ) : (
          <>
            {/* Google — only on signin/signup */}
            {(isSignIn || isSignUp) && (
              <>
                <button
                  onClick={handleGoogle}
                  style={{
                    width: "100%", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 10,
                    background: "#fff", border: "1.5px solid rgba(0,0,0,0.14)",
                    borderRadius: 14, padding: "13px 20px",
                    fontSize: 15, fontWeight: 700, color: "#1a2b1d",
                    cursor: "pointer", marginBottom: 16,
                    fontFamily: "var(--font-nunito), sans-serif",
                    transition: "background 0.15s",
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = "#f8f8f8")}
                  onMouseOut={e => (e.currentTarget.style.background = "#fff")}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
                  </svg>
                  Continue with Google
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.08)" }} />
                  <span style={{ fontSize: 13, color: "#9ab5a0", fontWeight: 600 }}>or</span>
                  <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.08)" }} />
                </div>
              </>
            )}

            {/* Form */}
            <form onSubmit={isSignUp ? handleSignUp : isReset ? handleReset : isMagic ? handleMagicLink : handleSignIn}>
              <input
                type="email"
                className="waitlist-input"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{ width: "100%", marginBottom: 10 }}
              />

              {(isSignIn || isSignUp) && (
                <input
                  type="password"
                  className="waitlist-input"
                  placeholder={isSignUp ? "Create a password" : "Password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ width: "100%", marginBottom: 10 }}
                />
              )}

              {error && (
                <div style={{ fontSize: 13, color: "#e53e3e", fontWeight: 600, marginBottom: 8 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="waitlist-btn"
                disabled={loading}
                style={{ width: "100%", opacity: loading ? 0.7 : 1 }}
              >
                {loading ? "…" : isSignUp ? "Create account" : isReset ? "Send reset link" : isMagic ? "Send magic link" : "Sign in"}
              </button>
            </form>

            {/* Footer links */}
            <div style={{ marginTop: 18, textAlign: "center", display: "flex", flexDirection: "column", gap: 8 }}>
              {isSignIn && (
                <>
                  <button onClick={() => switchMode("signup")} style={linkStyle}>
                    No account? <strong>Sign up</strong>
                  </button>
                  <button onClick={() => switchMode("magic")} style={linkStyle}>
                    Sign in without password
                  </button>
                  <button onClick={() => switchMode("reset")} style={linkStyle}>
                    Forgot password?
                  </button>
                </>
              )}
              {isSignUp && (
                <button onClick={() => switchMode("signin")} style={linkStyle}>
                  Already have an account? <strong>Sign in</strong>
                </button>
              )}
              {(isMagic || isReset) && (
                <button onClick={() => switchMode("signin")} style={linkStyle}>
                  ← Back to sign in
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const linkStyle: React.CSSProperties = {
  fontSize: 13, color: "#6a8f72", fontWeight: 600,
  background: "none", border: "none", cursor: "pointer",
  fontFamily: "var(--font-nunito), sans-serif",
};
